import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CoursesCatalogList } from "@/components/CoursesCatalogList";
import { cn } from "@/lib/cn";
import { CloseIcon } from "@/components/icons";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CoursesCatalogDrawer({ open, onClose }: Props): JSX.Element {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-30 bg-foreground/30 transition-opacity duration-200",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
        aria-hidden
      />
      <aside
        className={cn(
          "fixed top-0 left-0 z-40 w-[88vw] max-w-sm h-screen bg-card border-r border-border p-4 shadow-xl overflow-y-auto transition-transform duration-200",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Courses catalog"
      >
        <div className={cn(open && "cell-rise")}>
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-border-subtle">
            <h4 className="m-0 text-[13px] uppercase tracking-wider font-semibold">Courses</h4>
            <button
              onClick={onClose}
              className="bg-transparent border-0 text-muted-foreground cursor-pointer text-base px-2 rounded hover:bg-muted"
              aria-label="Close courses"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>
          <Link
            to="/courses"
            onClick={onClose}
            className="block rounded-control border border-border bg-muted/40 px-3 py-3 mb-2 hover:bg-muted transition-colors"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-foreground">Browse all courses</span>
              <span className="text-xs text-primary-soft shrink-0">Catalog →</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">See the full course catalog</p>
          </Link>
          <CoursesCatalogList onNavigate={onClose} />
        </div>
      </aside>
    </>
  );
}
